import React, { useState, useEffect, useCallback } from 'react';
import { useAddin } from '../App';
import Modal from './Modal';
import * as webhook from '../services/webhook';

/**
 * TimeEntryModal — log billable time to Clio.
 * Pre-populates description from email subject, date from today.
 * @param {{ isOpen: boolean, onClose: () => void }} props
 */
export default function TimeEntryModal({ isOpen, onClose }) {
    const { linkedMatter, itemId, conversationId, userEmail, subject } = useAddin();
    const [duration, setDuration] = useState('0.1');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);

    // Pre-populate on open
    useEffect(() => {
        if (isOpen) {
            setDescription(`[Email] ${subject || 'Email correspondence'}`);
            setDate(new Date().toISOString().split('T')[0]);
            setFeedback(null);
        }
    }, [isOpen, subject]);

    const handleSubmit = useCallback(async () => {
        const dur = parseFloat(duration);
        if (isNaN(dur) || dur <= 0) {
            setFeedback({ type: 'error', message: 'Duration must be greater than 0' });
            return;
        }

        if (!description.trim()) {
            setFeedback({ type: 'error', message: 'Description is required' });
            return;
        }

        setLoading(true);
        setFeedback(null);

        try {
            await webhook.logTimeEntry({
                outlookItemId: itemId,
                conversationId,
                userEmail,
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.number || linkedMatter?.matter_number,
                duration: dur,
                description: description.trim(),
                date,
            });
            setFeedback({ type: 'success', message: `${dur}h logged to Clio` });
            setTimeout(() => {
                setFeedback(null);
                onClose();
            }, 1500);
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Failed to log time entry' });
        } finally {
            setLoading(false);
        }
    }, [duration, description, date, itemId, conversationId, userEmail, linkedMatter, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Log Time Entry">
            {/* Matter context */}
            {linkedMatter && (
                <div className="info-block" style={{ padding: '8px 11px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--navy)' }}>
                        {linkedMatter.name || linkedMatter.matter_description}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--grey02)' }}>
                        {linkedMatter.number || linkedMatter.matter_number}
                    </div>
                </div>
            )}

            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">Duration (hours)</label>
                    <input
                        className="form-input"
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        id="time-duration"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Date</label>
                    <input
                        className="form-input"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        id="time-date"
                    />
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                    className="form-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the work performed..."
                    id="time-description"
                />
            </div>

            {/* Feedback */}
            {feedback && (
                <div className={`modal-feedback ${feedback.type}`}>
                    {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                </div>
            )}

            <button
                className="modal-submit"
                onClick={handleSubmit}
                disabled={loading}
                id="submit-time-btn"
            >
                {loading && <div className="btn-spinner" />}
                {loading ? 'Logging...' : 'Log Time Entry'}
            </button>
        </Modal>
    );
}
